import React from 'react';
import { Row, Col } from 'antd';
import { ShiftClosingDto } from '../../types/cashRegister';

export const getDifferencePrefix = (diff: number) => {
  if (diff === 0) return '';
  return diff > 0 ? '+' : '';
};

/** Sobrante (sistema > contado) se lee como error, faltante como advertencia. */
export const getDifferenceColor = (diff: number) => {
  if (diff === 0) return 'var(--color-success-text)';
  return diff > 0 ? 'var(--color-error-text)' : 'var(--color-warning-text)';
};

interface AmountBoxProps {
  label: string;
  counted: number;
  system: number;
  difference: number;
  formatCurrency: (value: number) => string;
  renderDifference: (diff: number) => React.ReactNode;
}

const AmountBox: React.FC<AmountBoxProps> = ({
  label,
  counted,
  system,
  difference,
  formatCurrency,
  renderDifference,
}) => (
  <div style={{
    textAlign: 'center',
    padding: '12px',
    background: 'var(--bg-tertiary)',
    color: 'var(--text-primary)',
    borderRadius: '8px',
  }}>
    <div style={{ color: 'var(--color-neutral-text)', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
      Contado: {formatCurrency(counted)}
    </div>
    <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
      Sistema: {formatCurrency(system)}
    </div>
    {renderDifference(difference)}
  </div>
);

interface TotalCellProps {
  label: string;
  isMobile: boolean;
  children: React.ReactNode;
}

const TotalCell: React.FC<TotalCellProps> = ({ label, isMobile, children }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{
      color: 'var(--color-neutral-text)',
      marginBottom: 4,
      fontSize: isMobile ? '12px' : '14px',
    }}>
      {label}
    </div>
    {children}
  </div>
);

interface ShiftClosingAmountsProps {
  shiftClosing: ShiftClosingDto;
  isMobile: boolean;
  formatCurrency: (value: number) => string;
  /** Cada modal dibuja la diferencia por método a su manera (span vs Statistic). */
  renderDifference: (diff: number) => React.ReactNode;
}

/**
 * Comparación contado-vs-sistema por método de pago + banda de totales.
 * Compartida por ViewShiftClosingModal y ReviewShiftClosingModal: eran copias
 * byte a byte y se desincronizaron en la migración de tema.
 */
const ShiftClosingAmounts: React.FC<ShiftClosingAmountsProps> = ({
  shiftClosing,
  isMobile,
  formatCurrency,
  renderDifference,
}) => {
  const totalDifference = shiftClosing.totalDifference || 0;
  const balanced = totalDifference === 0;

  return (
    <>
      <Row gutter={isMobile ? 8 : 16} style={{ marginBottom: isMobile ? 8 : 16 }}>
        <Col xs={24} sm={8}>
          <AmountBox
            label="Efectivo"
            counted={shiftClosing.countedCash}
            system={shiftClosing.systemCash}
            difference={shiftClosing.cashDifference}
            formatCurrency={formatCurrency}
            renderDifference={renderDifference}
          />
        </Col>

        <Col xs={24} sm={8}>
          <AmountBox
            label="Tarjetas"
            counted={shiftClosing.countedCardSales}
            system={shiftClosing.systemCardSales}
            difference={shiftClosing.cardDifference}
            formatCurrency={formatCurrency}
            renderDifference={renderDifference}
          />
        </Col>

        <Col xs={24} sm={8}>
          <AmountBox
            label="Transferencias"
            counted={shiftClosing.countedTransferSales}
            system={shiftClosing.systemTransferSales}
            difference={shiftClosing.transferDifference}
            formatCurrency={formatCurrency}
            renderDifference={renderDifference}
          />
        </Col>
      </Row>

      <Row gutter={isMobile ? 8 : 16} style={{ marginBottom: isMobile ? 8 : 16 }}>
        <Col xs={24} sm={12}>
          <AmountBox
            label="Otros Métodos"
            counted={shiftClosing.countedOtherSales}
            system={shiftClosing.systemOtherSales}
            difference={shiftClosing.otherDifference}
            formatCurrency={formatCurrency}
            renderDifference={renderDifference}
          />
        </Col>

        <Col xs={24} sm={12}>
          <AmountBox
            label="Propinas"
            counted={shiftClosing.countedTips}
            system={shiftClosing.systemTips}
            difference={shiftClosing.tipsDifference}
            formatCurrency={formatCurrency}
            renderDifference={renderDifference}
          />
        </Col>
      </Row>

      <div style={{
        background: balanced ? 'var(--bg-success-light)' : 'var(--bg-warning-light)',
        border: `1px solid ${balanced ? 'var(--border-success)' : 'var(--border-warning)'}`,
        color: 'var(--text-primary)',
        padding: isMobile ? '12px' : '16px',
        borderRadius: '8px',
        marginBottom: isMobile ? 12 : 16,
      }}>
        <Row gutter={isMobile ? 8 : 16}>
          <Col xs={24} sm={8}>
            <TotalCell label="Total Contado" isMobile={isMobile}>
              <div style={{
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: 'bold',
                color: 'var(--color-info-text)',
              }}>
                {formatCurrency(shiftClosing.countedTotal || 0)}
              </div>
            </TotalCell>
          </Col>
          <Col xs={24} sm={8}>
            <TotalCell label="Total Sistema" isMobile={isMobile}>
              <div style={{
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: 'bold',
                color: 'var(--color-info-text)',
              }}>
                {formatCurrency(shiftClosing.systemTotal || 0)}
              </div>
            </TotalCell>
          </Col>
          <Col xs={24} sm={8}>
            <TotalCell label="Diferencia Total" isMobile={isMobile}>
              <div style={{
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: 'bold',
                color: getDifferenceColor(totalDifference),
              }}>
                {getDifferencePrefix(totalDifference)}{formatCurrency(Math.abs(totalDifference))}
                {balanced ? ' ✓' : ''}
              </div>
            </TotalCell>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ShiftClosingAmounts;
